import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, useTrip, useUnsavedChanges, Notes, EntityLinkerModal, RelatedDocumentsSection, LinkEntitiesButton, RelatedEntitiesSection, GenericConfirmModal, entityLinkingService, EntityDetailLayout } from '@trip-planner-monorepo/shared';
import { travelService } from '../../services/travelService';
import AddTravel from '../AddTravel/AddTravel';
import { getTypeComponent } from './types';
import UploadModal from '@trip-planner-monorepo/documents/components/UploadModal';
import { FaEdit, FaPlane, FaTrain, FaCar, FaShip, FaBus, FaQuestion, FaFile } from 'react-icons/fa';
import './TravelDetails.css';

function TravelDetails() {
    const [travel, setTravel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [lastEditedTime, setLastEditedTime] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showLinkModal, setShowLinkModal] = useState(false);

    // Unlink Modal State
    const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);
    const [entityToUnlink, setEntityToUnlink] = useState(null);
    const [isUnlinking, setIsUnlinking] = useState(false);

    const { currentUser } = useAuth();
    const { tripId, travelId } = useParams();
    const navigate = useNavigate();
    const { effectiveUserId } = useTrip();

    // Fetch travel data
    useEffect(() => {
        if (!currentUser || !tripId || !travelId || !effectiveUserId) return;

        const fetchTravel = async () => {
            setLoading(true);
            try {
                const travelItems = await travelService.fetchTravel(effectiveUserId, tripId);
                const foundTravel = travelItems.find(t => t.id === travelId);
                if (!foundTravel) {
                    throw new Error('Travel item not found');
                }
                setTravel(foundTravel);
                setLastEditedTime(foundTravel.updatedAt ? new Date(foundTravel.updatedAt) : null);
            } catch (err) {
                setError('Failed to load travel details. Please try again.');
                console.error('Error fetching travel:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchTravel();
    }, [currentUser, tripId, travelId, effectiveUserId]);

    // Handle saving travel updates
    const handleSaveTravel = async (updatedTravelData) => {
        try {
            await travelService.updateTravel(effectiveUserId, tripId, travelId, updatedTravelData);
            // After updating, refresh the travel data
            const travelItems = await travelService.fetchTravel(effectiveUserId, tripId);
            const updatedTravel = travelItems.find(t => t.id === travelId);
            setTravel(updatedTravel);
            setLastEditedTime(new Date());
            setIsEditing(false);
        } catch (err) {
            setError('Failed to update travel details. Please try again.');
            console.error('Error updating travel:', err);
        }
    };

    // Save notes
    const handleSaveNotes = async (content) => {
        if (!travel) return;

        try {
            const updatedTravel = { ...travel, notes: content };
            await travelService.updateTravel(effectiveUserId, tripId, travelId, updatedTravel);
            setTravel(updatedTravel);
            setLastEditedTime(new Date());
        } catch (err) {
            setError('Failed to save notes. Please try again.');
            console.error('Error saving notes:', err);
        }
    };

    /**
     * Handles opening the document upload modal
     */
    const handleDocumentUpload = () => {
        setShowUploadModal(true);
    };

    /**
     * Handles document upload completion
     */
    const handleDocumentProcessed = async (result) => {
        console.log('Document processed from travel:', result);
        setShowUploadModal(false);
        // Refresh travel data to show the newly uploaded document
        const travelItems = await travelService.fetchTravel(effectiveUserId, tripId);
        const updatedTravel = travelItems.find(t => t.id === travelId);
        setTravel(updatedTravel);
    };

    /**
     * Handles entity link creation
     */
    const handleLinkCreated = async (links) => {
        console.log('Links created from travel:', links);
        // Refresh travel data to show the newly created links
        const travelItems = await travelService.fetchTravel(effectiveUserId, tripId);
        const updatedTravel = travelItems.find(t => t.id === travelId);
        setTravel(updatedTravel);
    };

    // Handle Unlink Request
    const handleUnlinkRequest = (entityType, entityId, entityName) => {
        setEntityToUnlink({ entityType, entityId, entityName });
        setUnlinkModalOpen(true);
    };

    // Handle Confirm Unlink
    const handleConfirmUnlink = async () => {
        if (!entityToUnlink) return;

        setIsUnlinking(true);
        try {
            await entityLinkingService.removeLink(
                effectiveUserId,
                tripId,
                'travel',
                travelId,
                entityToUnlink.entityType,
                entityToUnlink.entityId
            );

            // Refresh travel data
            const travelItems = await travelService.fetchTravel(effectiveUserId, tripId);
            const updatedTravel = travelItems.find(t => t.id === travelId);
            setTravel(updatedTravel);

            setUnlinkModalOpen(false);
            setEntityToUnlink(null);
        } catch (err) {
            console.error('Error unlinking entity:', err);
            setError('Failed to unlink entity. Please try again.');
        } finally {
            setIsUnlinking(false);
        }
    };

    if (!travel && !loading && !error) {
        return <div className="error-message">Travel item not found.</div>;
    }

    // Get icon for travel type
    const getTravelIcon = (type) => {
        switch (type) {
            case 'flight': return <FaPlane />;
            case 'train': return <FaTrain />;
            case 'car': return <FaCar />;
            case 'cruise': return <FaShip />;
            case 'bus': return <FaBus />;
            default: return <FaQuestion />;
        }
    };

    // Get type label
    const typeLabel = travel ? travel.type.charAt(0).toUpperCase() + travel.type.slice(1) : '';

    // Get the appropriate component for this travel type
    const TypeSpecificComponent = travel ? getTypeComponent(travel.type) : null;

    // Build the subtitle (type icon + type label)
    const subtitle = travel ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="travel-type-icon">{getTravelIcon(travel.type)}</span>
            {typeLabel}
        </span>
    ) : null;

    // Primary actions (Document Upload button)
    const primaryActions = !isEditing && travel ? [
        <button key="upload" className="edit-button document-upload-button" onClick={handleDocumentUpload}>
            <FaFile /> Document Upload
        </button>
    ] : [];

    // Secondary actions (Link Entities button)
    const secondaryActions = !isEditing && travel ? [
        <LinkEntitiesButton
            key="link"
            className="edit-button"
            onClick={() => setShowLinkModal(true)}
        />
    ] : [];

    // Title action (Edit button)
    const titleAction = !isEditing && travel ? (
        <button className="edit-button inline" onClick={() => setIsEditing(true)}>
            <FaEdit />
        </button>
    ) : null;

    // Main content (TypeSpecificComponent or AddTravel when editing)
    const mainContent = travel && (
        <>
            {isEditing && (
                <AddTravel
                    isOpen={isEditing}
                    onClose={() => setIsEditing(false)}
                    onSave={handleSaveTravel}
                    initialTravel={travel}
                />
            )}

            {!isEditing && TypeSpecificComponent && (
                <TypeSpecificComponent
                    travel={travel}
                    tripId={tripId}
                    RelatedDocumentsSection={RelatedDocumentsSection}
                    onUnlink={handleUnlinkRequest}
                />
            )}
        </>
    );

    // Related content (RelatedEntitiesSection)
    const relatedContent = !isEditing && travel && (
        <div className="entity-connections">
            <RelatedDocumentsSection
                contentBlocks={travel.contentBlocks}
                tripId={tripId}
                userId={effectiveUserId}
                hostEntityType="travel"
                hostEntityId={travelId}
                className="travel-related-documents"
                onUnlink={handleUnlinkRequest}
            />
            <RelatedEntitiesSection
                contentBlocks={travel.contentBlocks}
                tripId={tripId}
                userId={effectiveUserId}
                hostEntityType="travel"
                hostEntityId={travelId}
                excludeTypes={['document']}
                className="travel-related-entities"
                onUnlink={handleUnlinkRequest}
            />
        </div>
    );

    // Notes content
    const notesContent = !isEditing && travel && (
        <Notes
            content={travel.notes || ''}
            onSave={handleSaveNotes}
            lastEditedTime={lastEditedTime}
            className="travel-notes"
        />
    );

    return (
        <>
            <EntityDetailLayout
                className="travel-details"
                title={travel?.summary || 'Loading...'}
                subtitle={subtitle}
                titleAction={titleAction}
                primaryActions={primaryActions}
                secondaryActions={secondaryActions}
                mainContent={mainContent}
                relatedContent={relatedContent}
                notesContent={notesContent}
                loading={loading}
                error={error}
            />

            {/* Document Upload Modal */}
            {showUploadModal && travel && (
                <UploadModal
                    onClose={() => setShowUploadModal(false)}
                    onProcessed={handleDocumentProcessed}
                    currentEntity={{
                        type: 'travel',
                        id: travelId,
                        name: travel.summary || 'Unnamed Travel'
                    }}
                />
            )}

            {/* Entity Linker Modal */}
            {showLinkModal && travel && (
                <EntityLinkerModal
                    sourceEntity={{
                        type: 'travel',
                        id: travelId,
                        name: travel.summary || 'Unnamed Travel'
                    }}
                    onLinkCreated={handleLinkCreated}
                    onClose={() => setShowLinkModal(false)}
                    allowMultiSelect={true}
                />
            )}

            {/* Unlink Confirmation Modal */}
            <GenericConfirmModal
                isOpen={unlinkModalOpen}
                onClose={() => setUnlinkModalOpen(false)}
                onConfirm={handleConfirmUnlink}
                title="Unlink Entity"
                message={
                    entityToUnlink
                        ? `Are you sure you want to unlink "${entityToUnlink.entityName}"? This will remove the link in both directions.`
                        : 'Are you sure you want to unlink this entity?'
                }
                confirmText="Unlink"
                isDestructive={true}
                loading={isUnlinking}
            />
        </>
    );
}

export default TravelDetails; 